import React, { useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Typography,
  Space,
  message,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { formatBookingTime, formatAddress } from '../utils/formatters';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const Bookings = () => {
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [answerForm] = Form.useForm();
  const queryClient = useQueryClient();

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch bookings with pagination
  const { data: bookingsResponse, isLoading } = useQuery(
    ['provider-bookings', currentPage],
    async () => {
      const response = await api.get(`/provider/my-bookings?page=${currentPage}`);
      return response.data;
    },
    {
      enabled: !!localStorage.getItem('token') && !!localStorage.getItem('user'),
    },
  );

  // Extract bookings array and pagination info from response
  const bookings = bookingsResponse?.data || bookingsResponse || [];
  const totalBookings = bookingsResponse?.total || 0;

  // Fetch services with questions
  useQuery(
    'provider-services',
    async () => {
      const response = await api.get('/provider/services');
      return response.data?.data || response.data || [];
    },
    {
      enabled: !!localStorage.getItem('token') && !!localStorage.getItem('user'),
    },
  );

  // Confirm booking mutation
  const confirmMutation = useMutation(
    async (bookingId) => {
      const response = await api.post(`/provider/bookings/${bookingId}/confirm`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Booking confirmed successfully');
        setIsModalVisible(false);
        setSelectedBooking(null);
        queryClient.invalidateQueries('provider-bookings');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to confirm booking');
      },
    },
  );

  // Reject booking mutation
  const rejectMutation = useMutation(
    async ({ bookingId, reason }) => {
      const response = await api.post(`/provider/bookings/${bookingId}/reject`, { reason });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Booking rejected');
        setIsModalVisible(false);
        setSelectedBooking(null);
        queryClient.invalidateQueries('provider-bookings');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to reject booking');
      },
    },
  );

  // Start booking mutation
  const startMutation = useMutation(
    async (bookingId) => {
      const response = await api.post(`/provider/bookings/${bookingId}/start`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Booking started');
        setIsModalVisible(false);
        setSelectedBooking(null);
        queryClient.invalidateQueries('provider-bookings');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to start booking');
      },
    },
  );

  // Complete booking mutation
  const completeMutation = useMutation(
    async (bookingId) => {
      const response = await api.post(`/provider/bookings/${bookingId}/complete`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Booking completed');
        setIsModalVisible(false);
        setSelectedBooking(null);
        queryClient.invalidateQueries('provider-bookings');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to complete booking');
      },
    },
  );

  // Cancel booking mutation
  const cancelMutation = useMutation(
    async ({ bookingId, reason }) => {
      const response = await api.post(`/provider/bookings/${bookingId}/cancel`, { reason });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Booking cancelled');
        setIsModalVisible(false);
        setSelectedBooking(null);
        queryClient.invalidateQueries('provider-bookings');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to cancel booking');
      },
    },
  );

  const handleConfirmBooking = (bookingId) => {
    confirmMutation.mutate(bookingId);
  };

  const handleRejectBooking = (bookingId) => {
    Modal.confirm({
      title: 'Reject Booking',
      content: (
        <div>
          <p>Are you sure you want to reject this booking?</p>
          <Input.TextArea
            id="reject-reason"
            rows={3}
            placeholder="Enter reason for rejection (optional)"
          />
        </div>
      ),
      onOk() {
        const reason = document.getElementById('reject-reason')?.value || '';
        rejectMutation.mutate({ bookingId, reason });
      },
    });
  };

  const handleStartBooking = (bookingId) => {
    startMutation.mutate(bookingId);
  };

  const handleCompleteBooking = (bookingId) => {
    completeMutation.mutate(bookingId);
  };

  const handleCancelBooking = (bookingId) => {
    Modal.confirm({
      title: 'Cancel Booking',
      content: 'Are you sure you want to cancel this booking?',
      onOk() {
        cancelMutation.mutate({ bookingId, reason: 'Cancelled by provider' });
      },
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      confirmed: 'blue',
      in_progress: 'cyan',
      started: 'purple',
      completed: 'green',
      cancelled: 'red',
      rejected: 'red',
    };
    return colors[status] || 'default';
  };

  const getNextActions = (status) => {
    switch (status) {
      case 'pending':
        return ['confirm', 'reject'];
      case 'confirmed':
        return ['start'];
      case 'in_progress':
      case 'started':
        return ['complete'];
      case 'completed':
        return [];
      default:
        return [];
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (id) => <Tag>#{id}</Tag>,
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => {
        const user = record.user || record.customer;
        return (
          <div>
            <div>
              <Text strong>
                {user?.full_name || user?.name || record.customer_name || 'Unknown'}
              </Text>
            </div>
            <div style={{ fontSize: 12, color: '#888' }}>
              {user?.phone_number || record.contact_phone || record.customer_phone || 'N/A'}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Service',
      key: 'service',
      render: (_, record) => {
        const service = record.product_service || record.productService || record.service;
        return (
          <div>
            <Text>{service?.name || record.service_name || 'Unknown Service'}</Text>
            {service?.provider && (
              <div style={{ fontSize: 12, color: '#888' }}>
                Provider: {service.provider.full_name || service.provider.business_name || 'N/A'}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Date/Time',
      key: 'datetime',
      render: (_, record) => (
        <div>
          <div>
            <CalendarOutlined style={{ marginRight: 4 }} />{' '}
            {dayjs(record.booking_date).format('MMM D, YYYY')}
          </div>
          {record.booking_time && (
            <div style={{ fontSize: 12 }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} /> {record.booking_time}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('MMM D, YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const actions = getNextActions(record.status);
        return (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedBooking(record);
                setIsModalVisible(true);
              }}
            >
              View
            </Button>
            {actions.includes('confirm') && (
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleConfirmBooking(record.id)}
                loading={confirmMutation.isLoading}
              >
                Confirm
              </Button>
            )}
            {actions.includes('reject') && (
              <Button
                danger
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={() => handleRejectBooking(record.id)}
                loading={rejectMutation.isLoading}
              >
                Reject
              </Button>
            )}
            {actions.includes('start') && (
              <Button
                type="primary"
                size="small"
                icon={<ClockCircleOutlined />}
                onClick={() => handleStartBooking(record.id)}
                loading={startMutation.isLoading}
              >
                Start
              </Button>
            )}
            {actions.includes('complete') && (
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleCompleteBooking(record.id)}
                loading={completeMutation.isLoading}
              >
                Complete
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  // Statistics
  const stats = {
    total: totalBookings,
    pending: bookings?.filter((b) => b.status === 'pending').length || 0,
    confirmed: bookings?.filter((b) => b.status === 'confirmed').length || 0,
    completed: bookings?.filter((b) => b.status === 'completed').length || 0,
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <CalendarOutlined style={{ marginRight: '8px' }} />
        Bookings & Questions
      </Title>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="Total Bookings" value={stats.total} prefix={<CalendarOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Pending"
              value={stats.pending}
              valueStyle={{ color: '#faad14' }}
              prefix={<QuestionCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Confirmed"
              value={stats.confirmed}
              valueStyle={{ color: '#1890ff' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Completed"
              value={stats.completed}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Bookings Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={bookings}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: 15,
            total: totalBookings,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} bookings`,
          }}
        />
      </Card>

      {/* Booking Detail Modal */}
      <Modal
        title={selectedBooking ? `Booking #${selectedBooking.id}` : 'Booking Details'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedBooking(null);
          answerForm.resetFields();
        }}
        footer={
          selectedBooking ? (
            <Space>
              {getNextActions(selectedBooking.status).includes('confirm') && (
                <>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => handleRejectBooking(selectedBooking.id)}
                    loading={rejectMutation.isLoading}
                  >
                    Reject
                  </Button>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => handleConfirmBooking(selectedBooking.id)}
                    loading={confirmMutation.isLoading}
                  >
                    Confirm
                  </Button>
                </>
              )}
              {getNextActions(selectedBooking.status).includes('start') && (
                <Button
                  type="primary"
                  icon={<ClockCircleOutlined />}
                  onClick={() => handleStartBooking(selectedBooking.id)}
                  loading={startMutation.isLoading}
                >
                  Start Service
                </Button>
              )}
              {getNextActions(selectedBooking.status).includes('complete') && (
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleCompleteBooking(selectedBooking.id)}
                  loading={completeMutation.isLoading}
                >
                  Complete
                </Button>
              )}
              {selectedBooking.status !== 'completed' &&
                selectedBooking.status !== 'cancelled' &&
                selectedBooking.status !== 'rejected' &&
                !getNextActions(selectedBooking.status).includes('confirm') &&
                !getNextActions(selectedBooking.status).includes('start') &&
                !getNextActions(selectedBooking.status).includes('complete') && (
                  <Button
                    danger
                    onClick={() => handleCancelBooking(selectedBooking.id)}
                    loading={cancelMutation.isLoading}
                  >
                    Cancel Booking
                  </Button>
                )}
              <Button onClick={() => setIsModalVisible(false)}>Close</Button>
            </Space>
          ) : null
        }
        width={600}
      >
        {selectedBooking && (
          <div>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Text type="secondary">Customer Name:</Text>
                <div>
                  <Text strong>
                    {selectedBooking.user?.full_name || selectedBooking.user?.name || 'Unknown'}
                  </Text>
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Phone:</Text>
                <div>
                  {selectedBooking.user?.phone_number || selectedBooking.contact_phone || 'N/A'}
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Text type="secondary">Service:</Text>
                <div>
                  {selectedBooking.productService?.name ||
                    selectedBooking.product_service?.name ||
                    'Unknown Service'}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Status:</Text>
                <div>
                  <Tag color={getStatusColor(selectedBooking.status)}>{selectedBooking.status}</Tag>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Text type="secondary">Booking Date:</Text>
                <div>{dayjs(selectedBooking.booking_date).format('MMMM D, YYYY')}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Booking Time:</Text>
                <div>{formatBookingTime(selectedBooking.booking_time)}</div>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Text type="secondary">Address:</Text>
                <div>{formatAddress(selectedBooking)}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary">&nbsp;</Text>
              </Col>
            </Row>

            {selectedBooking.customer_message && (
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">Customer Message:</Text>
                <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 4, marginTop: 4 }}>
                  {selectedBooking.customer_message}
                </div>
              </div>
            )}

            {selectedBooking.questions && selectedBooking.questions.length > 0 && (
              <div>
                <Text type="secondary">Customer Questions:</Text>
                {selectedBooking.questions.map((q, idx) => (
                  <Card key={idx} size="small" style={{ marginTop: 8 }}>
                    <div>
                      <Text strong>Q: {q.question}</Text>
                    </div>
                    {q.answer ? (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary">A: </Text>
                        {q.answer}
                      </div>
                    ) : (
                      <div style={{ marginTop: 8 }}>
                        <Tag color="orange">Not Answered</Tag>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}

            {selectedBooking.status === 'pending' && (
              <div style={{ marginTop: 16, padding: 16, background: '#e6f7ff', borderRadius: 4 }}>
                <Text>Take action on this booking:</Text>
                <div style={{ marginTop: 8 }}>
                  <Space>
                    <Button type="primary" icon={<CheckCircleOutlined />}>
                      Confirm
                    </Button>
                    <Button danger icon={<CloseCircleOutlined />}>
                      Reject
                    </Button>
                  </Space>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Bookings;
